import { internalLogger } from '../../../../interface/v1/clientOsLogger';
import { getServices } from '../../../../infra/startup';
import matchPath from '../../../../utils/matchPath';
import isUserOnboardingPath from '../utils/isUserOnboardingPath';
import AbstractOperation from '../AbstractOperation';
import { DEFAULT_REDIRECT_STRATEGY } from './constants';

type TenantlessHandlerOptionsType = {
  userOnboardingPath?: string;
};

export default class TenantlessHandlerOperation extends AbstractOperation {
  _userOnboardingPath?: string;

  constructor(options?: TenantlessHandlerOptionsType) {
    super();
    this._userOnboardingPath = options?.userOnboardingPath;
  }

  public async process(): Promise<void> {
    // Get the current route
    const route = await this.getRouteByCurrentPath(false);

    // This operation only works for public routes
    if (!route?.public) {
      return;
    }

    // Check if user is logged in
    const { sessionService, tenantHandlerService } = getServices();
    const isLoggedIn = sessionService.isLoggedIn();
    if (!isLoggedIn) {
      return;
    }

    // Check if tenant handler is enabled
    const isTenantHandlerEnabled = tenantHandlerService.isEnabled();
    if (!isTenantHandlerEnabled) {
      return;
    }

    // Skip if user is on an onboarding path
    const onboardingPath = isUserOnboardingPath(this._userOnboardingPath);
    if (onboardingPath) {
      return;
    }

    // Check if user has a tenant
    const tenantId = tenantHandlerService.getTenantId();
    if (tenantId) {
      // User has a tenant, nothing to do
      return;
    }

    const config = tenantHandlerService.getForceTenantSelectionOnPublicRoutes();

    // Check if the feature is enabled in the manifest
    if (!config?.enabled) {
      return;
    }

    /* istanbul ignore next */
    internalLogger.debug.d4({
      className: 'TenantlessHandlerOperation',
      methodName: 'process',
      message:
        'User is logged in on a public route without tenant. Handling redirect.',
      variables: {
        config
      }
    });

    // Get the fallback strategy
    const fallbackStrategy = config.fallbackStrategy;
    const strategy = fallbackStrategy?.strategy || DEFAULT_REDIRECT_STRATEGY;

    // Currently only redirect strategy is supported
    if (strategy === DEFAULT_REDIRECT_STRATEGY) {
      const redirectUrl = fallbackStrategy?.redirectUrl || '/';

      // Don't redirect if we're already on the target path
      const { URLService } = getServices();
      // Strips out locale from the current path to avoid mismatches with redirectUrl which doesn't include locale
      const currentPath = URLService.getCurrentPathDescription().pathname;

      const isAlreadyOnTarget = matchPath(redirectUrl, {
        pathToCompare: currentPath
      });
      if (isAlreadyOnTarget) {
        /* istanbul ignore next */
        internalLogger.debug.d4({
          className: 'TenantlessHandlerOperation',
          methodName: 'process',
          message: 'Already on redirect target path, skipping redirect.',
          variables: { redirectUrl, currentPath }
        });
        return;
      }

      // Check if current path is in the block list
      const routesToBlockRedirect = fallbackStrategy?.routesToBlockRedirect;
      if (routesToBlockRedirect?.length > 0) {
        const isBlockedRoute = routesToBlockRedirect.some((blockedPath) =>
          matchPath(blockedPath, { pathToCompare: currentPath })
        );

        if (isBlockedRoute) {
          /* istanbul ignore next */
          internalLogger.debug.d4({
            className: 'TenantlessHandlerOperation',
            methodName: 'process',
            message:
              'Current path is in routesToBlockRedirect, skipping redirect.',
            variables: { currentPath, routesToBlockRedirect }
          });
          return;
        }
      }
      this.processRedirect(redirectUrl);
    }
  }

  private processRedirect(redirectUrl: string): void {
    /* istanbul ignore next */
    internalLogger.debug.d4({
      className: 'TenantlessHandlerOperation',
      methodName: 'processRedirect',
      message: `Redirecting tenantless user to: ${redirectUrl}`,
      variables: { redirectUrl }
    });

    // stateBuilder.setRedirectTo won't work on app startup
    const { navigationService } = getServices();
    navigationService.push(redirectUrl);
  }
}
